import type { ChartArea } from 'chart.js';

import { hexToRGBA } from '@/config/settings';

/**
 * Opacity at the top of the plot area and at the bottom.
 *
 * The band has to read as a backdrop rather than a second series: a filled area at a flat
 * opacity competes with the lines drawn over it, which is what the temperature band on the lice
 * charts did. Fading it out downwards keeps the value legible at the line while letting the
 * lice series stay readable through the bottom of the band.
 */
export const GRADIENT_TOP_OPACITY = 0.55;
export const GRADIENT_BOTTOM_OPACITY = 0.02;

/**
 * A `backgroundColor` for a filled line series that fades vertically from `color`.
 *
 * Chart.js resolves `backgroundColor` per render, so this returns a function rather than a
 * value: a `CanvasGradient` is bound to the canvas' coordinates and has to be rebuilt whenever
 * the plot area resizes. The gradient is cached and only rebuilt when the area's size actually
 * changes, so panning and zooming — which re-render constantly — do not allocate one per frame.
 *
 * The opacities default to the backdrop fade above; a context band that is the chart's own
 * background (`hideLine`) passes stronger ones through `MetricFillGradient`.
 */
/** The part of a Chart.js scriptable context the fill reads. */
export interface FillContext {
    chart: {
        ctx: Pick<CanvasRenderingContext2D, 'createLinearGradient'>;
        chartArea: Pick<ChartArea, 'top' | 'bottom' | 'left' | 'right'> | null | undefined;
    };
}

export type MetricFillResolver = (context: FillContext) => CanvasGradient | string;

export function verticalFadeFill(
    color: string,
    topOpacity: number = GRADIENT_TOP_OPACITY,
    bottomOpacity: number = GRADIENT_BOTTOM_OPACITY
): MetricFillResolver {
    let gradient: CanvasGradient | undefined;
    let width = 0;
    let height = 0;

    return (context: FillContext): CanvasGradient | string => {
        const { ctx, chartArea } = context.chart;

        // Chart.js resolves colours once before it has laid the plot area out. Returning the
        // flat top colour keeps that first paint from throwing; the next one gets the gradient.
        if (!chartArea) {
            return hexToRGBA(color, topOpacity);
        }

        const areaWidth = chartArea.right - chartArea.left;
        const areaHeight = chartArea.bottom - chartArea.top;

        if (!gradient || width !== areaWidth || height !== areaHeight) {
            width = areaWidth;
            height = areaHeight;

            gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, hexToRGBA(color, topOpacity));
            gradient.addColorStop(1, hexToRGBA(color, bottomOpacity));
        }

        return gradient;
    };
}
